import React, { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Label,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  useGetChecklistMappingsByProductQuery,
  useGetDocumentChecklistQuery,
  useBulkCreateChecklistMappingsMutation,
  useDeleteChecklistMappingMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface Mapping {
  id: number;
  product: number;
  checklist: number;
  checklist_name: string;
  // is_required: boolean;
  status: string;
  description?: string;
}

interface ProductDocumentChecklistSectionProps {
  productId: number;
  readOnly?: boolean;
}

const ProductDocumentChecklistSection: React.FC<
  ProductDocumentChecklistSectionProps
> = ({ productId, readOnly = false }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState<
    Array<{ value: number; label: string }>
  >([]);
  const [deletingMappingId, setDeletingMappingId] = useState<number | null>(
    null,
  );

  const { data: mappingsData, isLoading: loadingMappings } =
    useGetChecklistMappingsByProductQuery(productId);
  const { data: checklistData } = useGetDocumentChecklistQuery({
    page: 1,
    page_size: 500,
  });

  const [bulkCreate, { isLoading: bulkCreating }] =
    useBulkCreateChecklistMappingsMutation();
  const [deleteMapping] = useDeleteChecklistMappingMutation();

  const mappings: Mapping[] = Array.isArray(mappingsData)
    ? mappingsData
    : ((mappingsData as { results?: Mapping[] })?.results ?? []);
  const checklistList =
    (
      checklistData as {
        results?: Array<{ id: number; document_name: string }>;
      }
    )?.results ?? [];

  const checklistOptions = useMemo(
    () =>
      checklistList.map((c) => ({
        value: c.id,
        label: c.document_name,
      })),
    [checklistList],
  );

  const alreadyMappedIds = useMemo(
    () => new Set<number>(mappings.map((m) => m.checklist)),
    [mappings],
  );

  const optionsNotYetMapped = useMemo(
    () =>
      checklistOptions.filter((o) => !alreadyMappedIds.has(o.value as number)),
    [checklistOptions, alreadyMappedIds],
  );

  const handleAddDocuments = async () => {
    if (selectedChecklistIds.length === 0) {
      toast.warning("Select at least one document type");
      return;
    }
    try {
      await bulkCreate({
        product_id: productId,
        checklist_items: selectedChecklistIds.map((s) => ({
          checklist_id: s.value,
          // is_required: false,
          status: "pending",
        })),
      }).unwrap();
      toast.success("Document(s) added to product");
      setSelectedChecklistIds([]);
      setAddModalOpen(false);
    } catch (e: any) {
      toast.error(e?.data?.detail || "Failed to add documents");
    }
  };

  const handleRemoveMapping = async (mappingId: number) => {
    setDeletingMappingId(mappingId);
    try {
      await deleteMapping(mappingId).unwrap();
      toast.success("Document removed from product");
    } catch (e) {
      toast.error("Failed to remove");
    } finally {
      setDeletingMappingId(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Document Checklist</h5>
            {!readOnly && <Button
              color="primary"
              size="sm"
              onClick={() => setAddModalOpen(true)}
              disabled={bulkCreating || optionsNotYetMapped.length === 0}
            >
              <i className="ri-add-line align-middle me-1" />
              Add Documents
            </Button>}
          </div>
        </CardHeader>
        <CardBody>
          {loadingMappings ? (
            <div className="text-center py-3">
              <Spinner size="sm" /> Loading...
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-3 text-muted">
              No document types mapped.
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-bordered table-nowrap align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Document Type</th>
                    {/* <th>Required</th> */}
                    <th>Descripition</th>
                    {!readOnly && <th style={{ width: "100px" }}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id}>
                      <td>{m.checklist_name ?? "—"}</td>
                      {/* <td>{m.is_required ? "Yes" : "No"}</td> */}
                      <td>{m.description ?? "—"}</td>
                      {!readOnly && <td>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleRemoveMapping(m.id)}
                          disabled={deletingMappingId === m.id}
                        >
                          {deletingMappingId === m.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="ri-delete-bin-line" />
                          )}
                        </Button>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {!readOnly && <Modal
        isOpen={addModalOpen}
        toggle={() => {
          setAddModalOpen(false);
          setSelectedChecklistIds([]);
        }}
        size="md"
      >
        <ModalHeader toggle={() => setAddModalOpen(false)}>
          Add document types to product
        </ModalHeader>
        <ModalBody>
          <Label className="form-label">Select document type(s)</Label>
          <Select
            isMulti
            options={optionsNotYetMapped}
            value={selectedChecklistIds}
            onChange={(v: readonly { value: number; label: string }[] | null) =>
              setSelectedChecklistIds(v ? [...v] : [])
            }
            placeholder="Choose documents..."
          />
          {optionsNotYetMapped.length === 0 && (
            <p className="text-muted small mt-2">
              All available document types are already mapped to this product,
              or none exist. Add document types from the Document Checklist menu
              first.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAddModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleAddDocuments}
            disabled={bulkCreating || selectedChecklistIds.length === 0}
          >
            {bulkCreating ? (
              <>
                <Spinner size="sm" className="me-1" /> Adding...
              </>
            ) : (
              "Add selected"
            )}
          </Button>
        </ModalFooter>
      </Modal>}
    </>
  );
};

export default ProductDocumentChecklistSection;
